/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/ScopeSwitcher.tsx
 * DESCRIPTION: Interactive workspace scope switcher. Lets the user
 *              toggle the data lens between Global (platform-wide),
 *              Personal (their 1-person workspace), and any Team
 *              organization they belong to. Writes the selection to
 *              WorkspaceContext, which all data-fetching reads from.
 *
 * SCOPES:
 *   global       → platform-wide aggregate view (all users)
 *   personal     → user's personal workspace (auto-created org)
 *   organization → a team org the user is a member of
 *
 * BEHAVIOUR:
 *   - "Personal" is always available
 *   - "Global" is always available (per product decision)
 *   - Each team org appears if the user is a member
 *   - Switching to a team org also calls Clerk setActive() so
 *     useOrganization() elsewhere stays in sync
 *
 * DEPENDENCIES:
 *   - WorkspaceContext (scope state)
 *   - @clerk/react (useOrganizationList, useUser, setActive)
 *
 * ROADMAP: Phase 2.1 — Scope-Aware Frontend
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-05
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react';
import { useUser, useOrganizationList } from '@clerk/react';
import {
  ChevronDown,
  Check,
  User as UserIcon,
  Building2,
  Globe,
} from 'lucide-react';
import { useWorkspace, type WorkspaceScope } from '../contexts/WorkspaceContext';

/** Slug prefix that marks a personal workspace org (mirrors usePersonalOrg). */
const PERSONAL_SLUG_PREFIX = 'personal-';
const isPersonalOrg = (slug?: string | null): boolean =>
  !!slug && slug.startsWith(PERSONAL_SLUG_PREFIX);

const ScopeSwitcher: React.FC = () => {
  const { user } = useUser();
  const { userMemberships, isLoaded: membershipsLoaded, setActive } = useOrganizationList({
    userMemberships: true,
  });

  const { scope, activeOrgId, switchScope } = useWorkspace();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Close on outside click ──────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Derive available scopes ─────────────────────────────────────
  const memberships = membershipsLoaded ? userMemberships?.data || [] : [];
  const personalOrg = memberships.find(m => isPersonalOrg(m.organization.slug));
  const teamOrgs = memberships.filter(m => !isPersonalOrg(m.organization.slug));

  // ── Current label + color for the trigger pill ─────────────────
  const currentLabel =
    scope === 'global'
      ? 'Global'
      : scope === 'organization'
        ? (memberships.find(m => m.organization.id === activeOrgId)?.organization.name || 'Organization')
        : 'Personal';

  // ── Handlers ───────────────────────────────────────────────────
  const selectGlobal = () => {
    switchScope('global');
    setIsOpen(false);
  };

  const selectPersonal = () => {
    const personalId = personalOrg?.organization.id || null;
    if (personalId) {
      setActive({ organization: personalId });
    }
    switchScope('personal', personalId, 'Personal Workspace');
    setIsOpen(false);
  };

  const selectOrganization = (orgId: string, orgName: string) => {
    setActive({ organization: orgId });
    switchScope('organization', orgId, orgName);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger pill */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        aria-expanded={isOpen}
        aria-label="Switch workspace scope"
      >
        {scope === 'global' && <Globe className="w-3.5 h-3.5" />}
        {scope === 'personal' && <UserIcon className="w-3.5 h-3.5" />}
        {scope === 'organization' && <Building2 className="w-3.5 h-3.5" />}
        <span className="max-w-[120px] truncate">{currentLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              View data as
            </p>
          </div>

          <div className="py-1">
            {/* Global option */}
            <button
              onClick={selectGlobal}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Globe className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white">Global</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  Platform-wide overview
                </p>
              </div>
              {scope === 'global' && (
                <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              )}
            </button>

            {/* Personal option */}
            <button
              onClick={selectPersonal}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <UserIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white">Personal</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  My private workspace
                </p>
              </div>
              {scope === 'personal' && (
                <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              )}
            </button>

            {/* Team org options */}
            {teamOrgs.length > 0 && (
              <>
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                <div className="px-3 py-1.5">
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                    Organizations
                  </p>
                </div>
                {teamOrgs.map(({ organization }) => (
                  <button
                    key={organization.id}
                    onClick={() => selectOrganization(organization.id, organization.name)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                      <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {organization.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        @{organization.slug}
                      </p>
                    </div>
                    {scope === 'organization' && activeOrgId === organization.id && (
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeSwitcher;